/**
 * The selected conversation in the address bar, for every inbox surface.
 *
 * `conversation` carries a scoped id — `message:<uuid>`, `email:<inboxId>:<threadId>`,
 * `voice:<id>`. Older links carry a bare value under a surface-specific key, and
 * those are not the same kind of thing: `conversationId` wraps into a scoped id on
 * the spot, `emailExternalId` names a message whose thread is only known after a
 * lookup. So the read reports what it found and the surface resolves it.
 */

export const CONVERSATION_PARAM = "conversation"

export type InboxSelection =
  | { kind: "scoped"; id: string }
  | { kind: "legacy"; key: string; value: string }

export interface InboxUrlOptions {
  /** Keys older links carry — read as a fallback, always removed on write. */
  legacyKeys?: readonly string[]
}

/** `conversation` wins when both are present; the write then drops the legacy key. */
export function readInboxSelection(
  params: URLSearchParams,
  options: InboxUrlOptions = {},
): InboxSelection | null {
  const scoped = params.get(CONVERSATION_PARAM)
  if (scoped) return { kind: "scoped", id: scoped }
  for (const key of options.legacyKeys ?? []) {
    const value = params.get(key)
    if (value) return { kind: "legacy", key, value }
  }
  return null
}

/**
 * A copy of `url` with the selection written — `null` clears it — and every legacy
 * key gone either way. Pure: the caller decides what to do with the result, and
 * nothing here touches `window` or history.
 */
export function writeInboxSelection(
  url: URL,
  id: string | null,
  options: InboxUrlOptions = {},
): URL {
  const next = new URL(url)
  if (id) next.searchParams.set(CONVERSATION_PARAM, id)
  else next.searchParams.delete(CONVERSATION_PARAM)
  for (const key of options.legacyKeys ?? []) next.searchParams.delete(key)
  return next
}

/**
 * A link that opens one conversation in the unified inbox, from anywhere in the app.
 *
 * The page key and the parameter travel together here, so a surface linking into the inbox
 * cannot spell either differently from the page that reads them.
 */
export function unifiedInboxConversationHref(scopedConversationId: string): string {
  const params = new URLSearchParams({
    page: "unified-inbox",
    [CONVERSATION_PARAM]: scopedConversationId,
  })
  return `?${params.toString()}`
}

/** The same conversation in its messaging agent's own inbox, where the agent's controls are. */
export function messageAgentConversationHref(agentId: string, scopedConversationId: string): string {
  const params = new URLSearchParams({
    page: "message-conversations",
    agentId,
    [CONVERSATION_PARAM]: scopedConversationId,
  })
  return `?${params.toString()}`
}
